import { z } from 'zod';

import { DOCUMENT_DESTINATION_PRESENTATION } from '../documents/presentation.js';

export const ActSpecializationSchema = z.enum([
  'famille',
  'commercial',
  'civil',
  'penal',
  'social',
  'public',
  'jex',
  'immobilier',
  'assurances',
]);

/** Exact prompt-repository value for each stable specialization slug. */
export const ACT_SPECIALIZATION_LABELS = {
  famille: 'Droit de la Famille',
  commercial: 'Droit Commercial',
  civil: 'Droit Civil',
  penal: 'Droit Pénal',
  social: 'Droit Social',
  public: 'Droit Public',
  jex: "Procédures devant le Juge de l'exécution",
  // Libellé EXACT de prompts_library_add_in.specialisation (staging et prod, 102 lignes) : il part au backend
  // comme `specialisation` et l'appariement du prompt se fait à la casse près.
  immobilier: 'Droit Immobilier et de la Construction',
  // Spécialisation de premier niveau depuis le 2026-10-28 (Benoît) : les 9 actes du lot 1 d'Antoine Paul
  // étaient rangés sous « Droit Civil ». Même libellé dans `prompts_library_add_in.specialisation` et dans
  // `cases.procedure_type` (PROCEDURE_TYPES), que list_available_acts compare à la lettre.
  assurances: 'Droit des Assurances et de la Responsabilité Civile',
} as const;

export const ActWorkflowSchema = z.enum(['standard', 'riposte']);
export const ActSourceKindSchema = z.enum([
  'adverse_pleadings',
  'own_prior_pleading',
  'attacked_decision',
]);
export const ActSourceRequirementSchema = z.enum(['required', 'optional']);

export const ActSourceRequestUploadFieldSchema = z.enum([
  'adverse_pleadings_files',
  'our_prior_pleading_file',
  'decision_attaquee_files',
  'appeal_procedure_docs',
]);

/** Redis/HTTP metadata limits; file bytes remain in the request-local channel. */
export const ACT_SOURCE_REFERENCE_LIMIT = 100;
export const ACT_SOURCE_DOCUMENT_ID_MAX_CHARS = 64;
export const ACT_SOURCE_UPLOAD_FILE_NAME_MAX_CHARS = 255;
export const ACT_SOURCE_UPLOAD_MIME_TYPE_MAX_CHARS = 255;

/** A temporary upload is addressed exclusively by this server-issued UUID. */
export const ActSourceTemporaryImportReferenceSchema = z.object({
  referenceType: z.literal('temporary_import'),
  importId: z.string().uuid(),
}).strict();

/**
 * Data returned by the authenticated Web multipart import endpoint.
 *
 * The endpoint and the WebSocket deliberately share this opaque-reference
 * contract: the browser may keep the UUID, but never receives storage metadata
 * or file bytes to replay over the job protocol.
 */
export const ActSourceImportResponseSchema = ActSourceTemporaryImportReferenceSchema;

export const ACT_SOURCE_UPLOAD_FIELDS_BY_KIND = {
  adverse_pleadings: ['adverse_pleadings_files'],
  own_prior_pleading: ['our_prior_pleading_file'],
  attacked_decision: ['decision_attaquee_files', 'appeal_procedure_docs'],
} as const satisfies Record<
  z.infer<typeof ActSourceKindSchema>,
  readonly z.infer<typeof ActSourceRequestUploadFieldSchema>[]
>;

export const ActSourceReferenceSchema = z.discriminatedUnion('referenceType', [
  z.object({
    referenceType: z.literal('case_document'),
    documentId: z.string().max(ACT_SOURCE_DOCUMENT_ID_MAX_CHARS).uuid(),
  }).strict(),
  z.object({
    referenceType: z.literal('request_upload'),
    requestField: ActSourceRequestUploadFieldSchema,
    index: z.number().int().nonnegative(),
    fileName: z.string().min(1).max(ACT_SOURCE_UPLOAD_FILE_NAME_MAX_CHARS),
    mimeType: z.string().min(1).max(ACT_SOURCE_UPLOAD_MIME_TYPE_MAX_CHARS),
    size: z.number().int().nonnegative(),
  }).strict(),
  ActSourceTemporaryImportReferenceSchema,
]);

/** Browser-safe sources: Web never transports request-local Word upload data. */
export const ActSourceWebReferenceSchema = z.discriminatedUnion('referenceType', [
  z.object({
    referenceType: z.literal('case_document'),
    documentId: z.string().max(ACT_SOURCE_DOCUMENT_ID_MAX_CHARS).uuid(),
  }).strict(),
  ActSourceTemporaryImportReferenceSchema,
]);

export const ActSourceSelectionSchema = z.object({
  kind: ActSourceKindSchema,
  references: z.array(ActSourceReferenceSchema).max(ACT_SOURCE_REFERENCE_LIMIT),
}).strict().superRefine((source, ctx) => {
  const allowedUploadFields: readonly string[] = ACT_SOURCE_UPLOAD_FIELDS_BY_KIND[source.kind];
  for (const [referenceIndex, reference] of source.references.entries()) {
    if (reference.referenceType === 'request_upload'
      && !allowedUploadFields.includes(reference.requestField)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['references', referenceIndex, 'requestField'],
        message: `source kind ${source.kind} does not accept upload field ${reference.requestField}`,
      });
    }
  }
});

/**
 * Serializable source decision stored with an act-generation job.
 *
 * Upload bytes deliberately stay in the request-local `file_data` channel;
 * this snapshot only records enough metadata to explain and replay the launch
 * decision without turning Redis metadata into a second file store.
 */
export const ActSourceSelectionSnapshotSchema = z.object({
  actId: z.string().regex(/^act_[0-9a-f]{32}$/),
  workflow: ActWorkflowSchema,
  sources: z.array(ActSourceSelectionSchema).max(ActSourceKindSchema.options.length),
}).strict().superRefine((snapshot, ctx) => {
  const kinds = new Set<string>();
  for (const [sourceIndex, source] of snapshot.sources.entries()) {
    if (kinds.has(source.kind)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['sources', sourceIndex, 'kind'],
        message: `duplicate selected source kind: ${source.kind}`,
      });
    }
    kinds.add(source.kind);

    const references = new Set<string>();
    for (const [referenceIndex, reference] of source.references.entries()) {
      const key = reference.referenceType === 'case_document'
        ? `case_document:${reference.documentId}`
        : reference.referenceType === 'request_upload'
          ? `request_upload:${reference.requestField}:${reference.index}`
          : `temporary_import:${reference.importId}`;
      if (references.has(key)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['sources', sourceIndex, 'references', referenceIndex],
          message: `duplicate selected source reference: ${key}`,
        });
      }
      references.add(key);
    }
  }
});

const SourceCardinalityFieldsSchema = z.object({
  requirement: ActSourceRequirementSchema,
  minimum: z.number().int().nonnegative(),
  maximum: z.number().int().positive().nullable(),
  allowUpload: z.boolean(),
});

/**
 * Source selector contract rendered by Word/Web and enforced by the backend.
 *
 * The discriminated union binds every source kind to its only valid workspace
 * scope. This is deliberately stricter than two unrelated enums: swapping
 * `procedure` and `procedures` would otherwise compile but select wrong files.
 */
export const ActSourceRuleSchema = z.discriminatedUnion('kind', [
  SourceCardinalityFieldsSchema.extend({
    kind: z.literal('adverse_pleadings'),
    candidateScope: z.object({
      category: z.literal('partie_adverse'),
      subcategory: z.literal('actes_communiques'),
    }),
  }),
  SourceCardinalityFieldsSchema.extend({
    kind: z.literal('own_prior_pleading'),
    // `procedure` is the historical « Mes écritures » workspace category.
    candidateScope: z.object({ category: z.literal('procedure') }),
  }),
  SourceCardinalityFieldsSchema.extend({
    kind: z.literal('attacked_decision'),
    // `procedures` is the distinct « Juridiction et greffe » workspace category.
    candidateScope: z.object({ category: z.literal('procedures') }),
  }),
]).superRefine((rule, ctx) => {
  if (rule.requirement === 'required' && rule.minimum < 1) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['minimum'],
      message: 'a required act source must have minimum >= 1',
    });
  }
  if (rule.requirement === 'optional' && rule.minimum !== 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['minimum'],
      message: 'an optional act source must have minimum = 0',
    });
  }
  if (rule.maximum !== null && rule.maximum < rule.minimum) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['maximum'],
      message: 'an act source maximum must be null or >= minimum',
    });
  }
});

export const ActLengthOptionSchema = z.object({
  id: z.string().min(1),
  label: z.string().min(1),
  description: z.string().min(1),
});

export const ActCatalogEntrySchema = z.object({
  /** Stable identity: it must never be regenerated from Word array indexes. */
  id: z.string().regex(/^act_[0-9a-f]{32}$/),
  specialization: ActSpecializationSchema,
  /** Exact specialization value used by the prompt repository. */
  specializationLabel: z.string().min(1),
  /** Exact top-level Word procedure and prompt identity component. */
  procedure: z.string().min(1),
  subProcedure: z.string().min(1).nullable(),
  displayLabel: z.string().min(1),
  /** Exact `nom_acte` currently sent by Word, including the flattened sub-procedure. */
  backendName: z.string().min(1),
  workflow: ActWorkflowSchema,
  sources: z.array(ActSourceRuleSchema),
  presentation: z.object({
    lengthOptions: z.array(ActLengthOptionSchema).min(1),
    isNew: z.boolean(),
  }),
}).superRefine((entry, ctx) => {
  const expectedSpecializationLabel = ACT_SPECIALIZATION_LABELS[entry.specialization];
  if (entry.specializationLabel !== expectedSpecializationLabel) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['specializationLabel'],
      message: `specialization=${entry.specialization} requires specializationLabel=${expectedSpecializationLabel}`,
    });
  }

  const kinds = new Set<string>();
  for (const [index, source] of entry.sources.entries()) {
    if (kinds.has(source.kind)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['sources', index, 'kind'],
        message: `duplicate act source kind: ${source.kind}`,
      });
    }
    kinds.add(source.kind);
  }
});

/** Slim canonical entry returned to interactive catalogue consumers. */
export const AvailableActCatalogEntrySchema = z.object({
  id: z.string().regex(/^act_[0-9a-f]{32}$/),
  specialization: ActSpecializationSchema,
  specializationLabel: z.string().min(1),
  procedure: z.string().min(1),
  subProcedure: z.string().min(1).nullable(),
  displayLabel: z.string().min(1),
  backendName: z.string().min(1),
  workflow: ActWorkflowSchema,
  /**
   * Optional only while backend and Web deploy independently. New backends
   * always emit the array; a missing value makes the Web launcher fail closed.
   */
  sources: z.array(ActSourceRuleSchema).optional(),
  available: z.boolean(),
  unavailableReason: z.string().min(1).nullable(),
});

export const ActPromptIdentitySchema = z.object({
  backendName: z.string().min(1),
  specialization: z.string().min(1),
  procedure: z.string().min(1),
});

export const CanonicalActLaunchErrorSchema = z.discriminatedUnion('code', [
  z.object({
    code: z.literal('UNKNOWN_CANONICAL_ACT'),
    message: z.string().min(1),
    actId: z.string().min(1),
    expectedIdentity: ActPromptIdentitySchema.nullable(),
  }),
  z.object({
    code: z.literal('EXACT_PROMPT_UNAVAILABLE'),
    message: z.string().min(1),
    actId: z.string().min(1),
    expectedIdentity: ActPromptIdentitySchema,
  }),
  z.object({
    code: z.literal('AMBIGUOUS_EXACT_PROMPT'),
    message: z.string().min(1),
    actId: z.string().min(1),
    expectedIdentity: ActPromptIdentitySchema,
    duplicateLevel: z.enum(['cabinet', 'global']),
    duplicateCount: z.number().int().min(2),
  }),
]);

/** Flat, validated registry. Duplicate identities fail when the module loads. */
export const ActCatalogSchema = z.array(ActCatalogEntrySchema).superRefine((entries, ctx) => {
  const ids = new Set<string>();
  const positions = new Set<string>();

  for (const [index, entry] of entries.entries()) {
    if (ids.has(entry.id)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [index, 'id'],
        message: `duplicate canonical act id: ${entry.id}`,
      });
    }
    ids.add(entry.id);

    const position = [
      entry.specialization,
      entry.procedure,
      entry.subProcedure ?? '',
      entry.displayLabel,
    ].join('\u0000');
    if (positions.has(position)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [index],
        message: `duplicate canonical act position: ${entry.id}`,
      });
    }
    positions.add(position);
  }
});

export type ActSpecialization = z.infer<typeof ActSpecializationSchema>;
export type ActWorkflow = z.infer<typeof ActWorkflowSchema>;
export type ActSourceKind = z.infer<typeof ActSourceKindSchema>;
export type ActSourceRequirement = z.infer<typeof ActSourceRequirementSchema>;
export type ActSourceRequestUploadField = z.infer<typeof ActSourceRequestUploadFieldSchema>;
export type ActSourceTemporaryImportReference = z.infer<typeof ActSourceTemporaryImportReferenceSchema>;
export type ActSourceImportResponse = z.infer<typeof ActSourceImportResponseSchema>;
export type ActSourceReference = z.infer<typeof ActSourceReferenceSchema>;
export type ActSourceWebReference = z.infer<typeof ActSourceWebReferenceSchema>;
export type ActSourceSelection = z.infer<typeof ActSourceSelectionSchema>;
export type ActSourceSelectionSnapshot = z.infer<typeof ActSourceSelectionSnapshotSchema>;
export type ActSourceRule = z.infer<typeof ActSourceRuleSchema>;
export type ActLengthOption = z.infer<typeof ActLengthOptionSchema>;
export type ActCatalogEntry = z.infer<typeof ActCatalogEntrySchema>;
export type AvailableActCatalogEntry = z.infer<typeof AvailableActCatalogEntrySchema>;
export type ActPromptIdentity = z.infer<typeof ActPromptIdentitySchema>;
export type CanonicalActLaunchError = z.infer<typeof CanonicalActLaunchErrorSchema>;

/**
 * Vocabulaire humain d'une source d'acte — le SEUL endroit où une règle du
 * catalogue reçoit un nom lisible par l'avocat ou par l'agent.
 *
 * WHY ici et pas dans le composant : ces libellés étaient jusqu'ici recopiés
 * dans quatre endroits indépendants (les trois sélecteurs de la carte Web, et
 * `sourceLabel()` dans `redactionSourceSelection`), et un message d'erreur était
 * routé vers le bon bloc UI en cherchant la sous-chaîne « propre écriture » dans
 * un texte français. Renommer une source cassait donc l'affichage sans erreur de
 * type ni test rouge. Le libellé devient une donnée du contrat partagé : le
 * backend l'emploie pour dire à l'agent ce qui manque, la carte pour titrer son
 * sélecteur, et les deux disent exactement le même mot à l'avocat.
 */
export interface ActSourcePresentation {
  /** Titre du sélecteur, au pluriel neutre. */
  readonly label: string;
  /**
   * Groupe nominal COMPLET, article inclus : « une écriture adverse », « votre
   * écriture précédente ».
   *
   * WHY l'article voyage avec le mot plutôt que d'être ajouté par l'appelant :
   * un `Déposez la ${singulier}` produit « Déposez la écriture adverse », et le
   * genre change d'une source à l'autre — un jugement, une décision. Aucune
   * phrase construite hors d'ici ne peut être correcte pour les trois.
   */
  readonly singularIndefini: string;
  /**
   * Où la pièce se range, article inclus : « l'onglet « Mes écritures » ». Se
   * lit derrière n'importe quelle préposition — « dans », « de », « vers » —
   * sans jamais produire « dans le onglet ».
   */
  readonly location: string;
}

/**
 * Le droit public renomme deux onglets du dossier (« Mes mémoires »,
 * « Productions du défendeur » — cf. `DossierView`), et l'appel désigne une
 * décision attaquée là où le judiciaire parle d'un jugement de première
 * instance. Une phrase qui nommerait le mauvais onglet enverrait l'avocat
 * chercher au mauvais endroit : le drapeau n'est donc pas cosmétique.
 */
export function actSourcePresentation(
  kind: ActSourceKind,
  options: { publicLaw?: boolean } = {},
): ActSourcePresentation {
  const publicLaw = options.publicLaw === true;
  if (kind === 'adverse_pleadings') {
    return {
      label: publicLaw ? 'Productions du défendeur' : 'Écritures adverses',
      singularIndefini: publicLaw ? 'une production du défendeur' : 'une écriture adverse',
      location: publicLaw
        ? "l'onglet « Productions du défendeur »"
        : "l'onglet « Partie adverse », dossier « Écritures adverses »",
    };
  }
  if (kind === 'own_prior_pleading') {
    return {
      label: publicLaw ? 'Votre mémoire précédent' : 'Votre écriture précédente',
      singularIndefini: publicLaw ? 'votre mémoire précédent' : 'votre écriture précédente',
      location: publicLaw ? "l'onglet « Mes mémoires »" : "l'onglet « Mes écritures »",
    };
  }
  return {
    label: publicLaw ? 'Décision(s) attaquée(s)' : 'Jugement(s) de 1ère instance',
    singularIndefini: publicLaw ? 'une décision attaquée' : 'un jugement de 1ère instance',
    location: `l'onglet « ${DOCUMENT_DESTINATION_PRESENTATION.procedures.label} »`,
  };
}

/**
 * Cardinalité d'une règle, dite en français, sans jamais fabriquer un pluriel
 * bancal : l'ancien code concaténait un « s » APRÈS le libellé entier et aurait
 * produit « au moins 2 écriture adverses ».
 */
export function actSourceCardinalityLabel(rule: Pick<ActSourceRule, 'requirement' | 'minimum' | 'maximum'>): string {
  if (rule.requirement === 'optional') return 'facultatif';
  if (rule.maximum === 1) return 'une seule, obligatoire';
  return rule.minimum > 1 ? `${rule.minimum} minimum` : '1 minimum';
}
